export interface ISpotBiddingConfigState {
  spotBiddingConfig: ISpotBiddingConfig
  fetching: boolean
  error?: string
}

export enum RATE_TYPE {
  allIn = 'ALL_IN',
  linehaul = 'LINEHAUL',
}

export enum CONFIG_TYPE {
  default = 'DEFAULT',
  global = 'GLOBAL',
  shipper = 'SHIPPER',
  bucket = 'BUCKET',
}

export enum MODIFY_PAST_PICKUP {
  doNotBid = 'do_not_bid',
  setToToday = 'set_to_today',
  setToTomorrow = 'set_to_tomorrow',
  setToTwoDays = 'set_to_2_business_days',
}

export enum DAT_TYPE {
  dat = 'DAT',
  syntheticDat = 'Synthetic DAT',
}

export interface ISpotBiddingConfig {
  id?: string
  shipperApplication?: IShipperApplication
  rateType?: RATE_TYPE
  configType?: CONFIG_TYPE
  fuelRpm?: string
  spotBiddingRules: ISpotBiddingRules
  configName?: string
  shipperApplications: IShipperApplication[]
  baselineRuleAction?: RULE_ACTION
  baselineRulePremiumAmount?: string
  baselineRulePremiumType?: SPOT_PREMIUM_TYPE
  modifyPastPickupType?: MODIFY_PAST_PICKUP
  namespace?: string
}

export interface ISpotBiddingRules {
  baselineRule?: ISpotBiddingRule
  globalBaselineRule?: ISpotBiddingRule
  sameDayRule: ISpotBiddingRule[]
  weekendRule?: ISpotBiddingRule
  equipmentRules: ISpotBiddingRule[]
  mileageRules: ISpotBiddingRule[]
  geographicInboundRules: ISpotBiddingRule[]
  geographicOutboundRules: ISpotBiddingRule[]
  laneGeographicRules: ISpotBiddingRule[]
  contractRules: ISpotBiddingRule[]
  countLoadsQuotedRules: ISpotBiddingRule[]
  laneTierRules: ISpotBiddingRule[]
  distanceToMarketCenterRules: ISpotBiddingRule[]
  dateRules: ISpotBiddingRule[]
  dropRules: ISpotBiddingRule[]
  proximalHourRules: ISpotBiddingRule[]
  geoDateRules: ISpotBiddingRule[]
  onlyBidOnRules: ISpotBiddingRule[]
  appointmentTimeMileagePickupRules: ISpotBiddingRule[]
  appointmentTimeMileageDeliveryRules: ISpotBiddingRule[]
  carrierWaitTimeRules: ISpotBiddingRule[]
  multiStopRules: ISpotBiddingRule[]
  transitDifferenceRules: ISpotBiddingRule[]
}

export interface IShipperApplication {
  id: string
  name: string
}

export enum RULE_ACTION {
  dontBid = 'DONT_BID',
  applyPremium = 'APPLY_PREMIUM',
  onlyBidOn = 'ONLY_BID_ON',
}

export interface ISpotBiddingRule {
  id: string
  ruleAction: RULE_ACTION
  premiumType?: SPOT_PREMIUM_TYPE
  conditionQualifier?: string
  startAt?: string
  endAt?: string
  spotPremium?: ISpotPremium
  geographicCriteria?: IGeographicCriteria
  laneGeographicCriteria?: ILaneGeographicCriteria
  contractCriteria?: IContractCriteria
  countLoadsQuotedCriteria?: ICountLoadsQuotedCriteria
  laneTierCriteria?: ILaneTierCriteria
  distanceToMarketCenterCriteria?: IDistanceToMarketCenterCriteria
  mileageCriteria?: IMileageCriteria
  dateCriteria?: IDateCriteria
  proximalHourCriteria?: IProximalHourCriteria
  appointmentTimeCriteria?: IAppointmentTimeCriteria
  carrierWaitTimeCriteria?: ICarrierWaitTimeCriteria
  stopSpecificDropCriteria?: IStopSpecificDropCriteria
  transitDifferenceCriteria?: ITransitDifferenceCriteria
  ruleStartTimeCriteria?: IRuleStartTimeCriteria
  isBaseline: boolean
  geographicCriteriaId?: string
  mileageCriteriaId?: string
  dateCriteriaId?: string
  isDrop?: boolean
  isMultiStop?: boolean
  isSameDay: boolean
  isWeekend: boolean
  parentEquipmentKey?: PARENT_EQUIPMENT_KEY
  onlyBidOnCriteria?: IOnlyBidOnCriteria
  isSamePickupDelivery?: boolean
  isDifferentPickupDelivery?: boolean
  spotBiddingConfig?: ISpotBiddingConfig
  reason: string
}

export enum PARENT_EQUIPMENT_KEY {
  dryVan = 'dry_van',
  reefer = 'reefer',
}

export enum SPOT_PREMIUM_TYPE {
  percentage = 'PERCENTAGE',
  total = 'TOTAL',
}

export interface ISpotPremium {
  premiumType: SPOT_PREMIUM_TYPE
  amount: string
}

export enum GEOGRAPHIC_CRITERIA_TYPE {
  cityState = 'CITY, STATE',
  datMarket = 'DAT MARKET',
  state = 'STATE',
  anywhere = 'ANYWHERE',
}

export enum ONLY_BID_ON_CRITERIA {
  sameDay = 'Same Day',
  drop = 'Drop',
  weekend = 'Weekend',
  multiStop = 'Multi Stop',
}

export enum LOCATION_TYPE {
  pickup = 'pickup',
  delivery = 'delivery',
}

export enum CONDITION_QUALIFIER {
  is = 'Is',
  isNot = 'Is Not',
}

export enum RANGE_TYPE {
  more = 'more',
  less = 'less',
  between = 'between',
}

export enum DAY_OR_HOUR {
  days = 'Days',
  hours = 'Hours',
}

export enum AWARD_TYPE {
  primary = 'primary',
  backup = 'backup',
  published = 'published',
}

export interface IGeographicCriteria {
  criteriaType: GEOGRAPHIC_CRITERIA_TYPE
  value: string
  isInbound: boolean
}

export interface ILaneGeographicCriteria {
  pickupType: GEOGRAPHIC_CRITERIA_TYPE
  pickupValue: string
  deliveryType: GEOGRAPHIC_CRITERIA_TYPE
  deliveryValue: string
}

export interface IContractCriteria {
  awardType: AWARD_TYPE
}

export interface ICountLoadsQuotedCriteria {
  pickupType: GEOGRAPHIC_CRITERIA_TYPE
  pickupValue: string
  deliveryType: GEOGRAPHIC_CRITERIA_TYPE
  deliveryValue: string
  rangeStart?: string
  rangeEnd?: string
  isSamePickupDelivery?: boolean
  isDifferentPickupDelivery?: boolean
}

export interface IStopSpecificDropCriteria {
  pickupDropTrailer: boolean
  deliveryDropTrailer: boolean
  showStopSpecificCheckbox?: boolean
}

export interface IRuleStartTimeCriteria {
  rangeStart?: string
}

export interface ITransitDifferenceCriteria {
  timeUnit: string
  timeRangeStart: string
  timeRangeEnd: string
}

export interface IOnlyBidOnCriteria {
  isDrop: boolean | null
  isSameDay: boolean | null
  isWeekend: boolean | null
  isMultiStop: boolean | null
}

export interface ILaneTierCriteria {
  tier: string
}

export interface IDistanceToMarketCenterCriteria {
  locationType: LOCATION_TYPE
  rangeStart?: string
  rangeEnd?: string
}

export interface IMileageCriteria {
  rangeStart?: string
  rangeEnd?: string
}

export interface IDateCriteria {
  rangeStart?: string
  rangeEnd?: string
}

export interface IProximalHourCriteria {
  dayOrHour: DAY_OR_HOUR
  rangeStart?: string
  rangeEnd?: string
}

export interface IAppointmentTimeCriteria {
  pickupRangeStart: string
  pickupRangeEnd: string
  deliveryRangeStart: string
  deliveryRangeEnd: string
  timezoneOverride?: string
}

export interface ICarrierWaitTimeCriteria {
  rangeStart: string
  rangeEnd: string
}

export enum SPOT_BIDDING_RULES_COLUMNS {
  ruleType = 'Rule Type',
  type = 'Type',
  value = 'Value',
  premium = '% Premium',
  reason = 'Reason',
}

export enum COST_STRATEGY {
  'defaultSpotControlPanelConfigs' = 'Attribute-Based Pricing Strategy Chosen',
  'automatedStrikePrice' = 'Margin-Optimized Pricing Strategy Chosen',
  'bidAtCost' = 'Bid-At-Cost Strategy Chosen',
  'other' = 'Other Strategy Chosen',
}

export const STRATEGY_CHOSEN = {
  spotControl: 'Default spot control panel configs',
  strikePrice: 'Automated strike price',
  revenuePlusMargin: 'Revenue plus margin',
}

export const RULE_LOCATIONS = {
  spotControlPanel: 'Spot Control Panel',
  strikePriceControlPanel: 'Strike Price Control Panel',
  costControlPanel: 'Cost Control Panel',
  customCode: 'Engineering Code',
}

export enum SPOT_RULES_COLUMNS {
  status = 'Status',
  premiumOrCost = 'Premium or Cost Applied',
  rule = 'Rule',
  ruleDetail = 'Rule Detail',
  ruleLocation = 'Rule Location',
}

export interface ISpotBiddingRulePayload {
  rule?: ISpotBiddingRule
}

export interface IModalsState {
  showAddModal: boolean
  showDeleteModal: boolean
  modalPayload?: ISpotBiddingRulePayload
  inProgress: boolean
  error?: string
}

export enum SORT_FIELD {
  ID = 'id',
  CONFIG_NAME = 'config_name',
}

export enum SORT_ORDER {
  ASC = 'asc',
  DESC = 'desc',
}

export interface ISpotBiddingConfigsFilterState {
  configType?: CONFIG_TYPE
  configName?: string
  shipperName?: string
}
export interface ISpotBiddingConfigsParams {
  pageCount: number
  perPage: number
  filters?: ISpotBiddingConfigsFilterState
  sortField?: SORT_FIELD
  sortOrder?: SORT_ORDER
}

export interface ISpotBiddingConfigsState {
  spotBiddingConfigs: ISpotBiddingConfig[]
  fetching: boolean
  error?: string
  totalCount: number
  params: ISpotBiddingConfigsParams
}

export enum SPOT_BIDDING_CONFIGS_COLUMNS {
  shipperSegment = 'Shipper Segment',
  shippers = 'Shipper Name(s)',
  premium = 'Premium',
  ruleType = 'Rule',
  actions = 'Actions',
}
